import { useTranslation } from 'react-i18next'
import { Color, Font, IconName, MenuItemData, Radius, Spacing } from 'zens-storybook'
import { useState } from 'react'
import { StyleSheet } from 'react-native'
import { AccessType } from 'zens-store'
import { usePathname, useNumberParams, Href } from 'zens-router'

import { usePermissions } from './permissions'

const styles = StyleSheet.create({
  settings: {
    flex: 1,
    justifyContent: 'flex-end',
    marginBottom: Spacing.L,
    marginHorizontal: Spacing.S,
  },
  settingsRow: {
    borderRadius: Radius.M,
    backgroundColor: Color.sleepingGrey,
    overflow: 'hidden',
  },
  settingsTitle: {
    fontFamily: Font.medium,
  },
})

interface Item extends Omit<MenuItemData, 'key' | 'items' | 'icon'> {
  icon?: string
  key?: string // Used if sub items have same routeName
  href?: Href
  activeRoute?: string // used if item has no route to be pushed and only opens subitems
  divider?: boolean
  items?: Item[]
  active?: boolean
}

export const useMenu = (initParams?: Record<string, string | number>, otherItems?: Item[]) => {
  const [activeRoute, setActiveRoute] = useState<string>()
  const { t } = useTranslation()
  const pathname = usePathname()
  const [{ companyId, buildingId, floorId }] = useNumberParams()
  const { hasPermission, isSysAdmin } = usePermissions(companyId)

  const menuDataForItem = (item: Item) => {
    const { icon, href, key, title, activeRoute: itemActiveRoute, items, active, ...otherItem } = item
    const hrefPathname = typeof href === 'string' ? href : href?.pathname
    const menuItem: MenuItemData = {
      ...otherItem,
      icon: icon as IconName,
      key: key || title,
      title,
      items: items?.map(menuDataForItem),
      href,
      onPress: href
        ? () => {
            hrefPathname !== activeRoute && setActiveRoute(undefined)
          }
        : itemActiveRoute
          ? () => setActiveRoute(activeRoute === itemActiveRoute ? undefined : itemActiveRoute)
          : undefined,
      selected: pathname?.replace(/\?.*/, '') === hrefPathname,
      active: active === false ? false : active || !!(hrefPathname && pathname?.startsWith(hrefPathname)),
      opened: !!activeRoute && itemActiveRoute === activeRoute,
      hasItems:
        (activeRoute && itemActiveRoute === activeRoute) || (hrefPathname && pathname?.startsWith(hrefPathname))
          ? false
          : undefined,
    }
    return menuItem
  }
  const items: Item[] = [
    {
      title: t('Home'),
      icon: 'home',
      href: `/companies/${companyId}`,
      active: false,
    },
    ...(hasPermission(AccessType.Read, 'insights') || hasPermission(AccessType.Read, 'kiosk')
      ? [
          {
            title: t('Insights'),
            icon: 'graph',
            href: `/companies/${companyId}/insights`,
            hasItems: true,
            items: buildingId
              ? [
                  ...(hasPermission(AccessType.Read, 'insights')
                    ? [
                        {
                          title: t('Over time'),
                          href: {
                            pathname: `/companies/${companyId}/insights/${buildingId}/time`,
                            params: floorId ? { floorId } : undefined,
                          },
                        },
                        {
                          title: t('Historical heatmap'),
                          href: `/companies/${companyId}/insights/${buildingId}/floors/${floorId}`,
                          active: pathname === `/companies/${companyId}/insights/${buildingId}/floors/${floorId}`,
                        },
                        {
                          title: t('Trends'),
                          activeRoute: 'InsightsTrend',
                          key: 'trends',
                          items: [
                            {
                              title: t('By floor'),
                              href: {
                                pathname: `/companies/${companyId}/insights/${buildingId}/trend/floor`,
                                params: floorId ? { floorId } : undefined,
                              },
                              key: 'trendsfloor',
                            },
                            {
                              title: t('By zone'),
                              href: {
                                pathname: `/companies/${companyId}/insights/${buildingId}/trend/zone`,
                                params: floorId ? { floorId } : undefined,
                              },
                              key: 'trendszone',
                            },
                            {
                              title: t('By workspace type'),
                              href: {
                                pathname: `/companies/${companyId}/insights/${buildingId}/trend/workspacetype`,
                                params: floorId ? { floorId } : undefined,
                              },
                              key: 'trendsworkspacetype',
                            },
                            {
                              title: t('By weekday'),
                              href: {
                                pathname: `/companies/${companyId}/insights/${buildingId}/trend/weekday`,
                                params: floorId ? { floorId } : undefined,
                              },
                              key: 'trendsweekday',
                            },
                          ],
                        },
                      ]
                    : []),
                  {
                    title: t('Live'),
                    href: `/companies/${companyId}/insights/${buildingId}/floors/${floorId}/live`,
                  },
                ]
              : undefined,
          },
          {
            title: t('AI Data Assistant'),
            icon: 'bullhorn',
            href: `/companies/${companyId}/assistant`,
          },
        ]
      : []),
    ...(hasPermission(AccessType.Read, 'workspace')
      ? [
          {
            title: t('Workspaces'),
            icon: 'desk',
            href: `/companies/${companyId}/workspaces`,
            items: [
              ...(hasPermission(AccessType.Read, 'schedule')
                ? [{ title: t('Schedules'), href: `/companies/${companyId}/workspaces/schedules` }]
                : []),
              ...(hasPermission(AccessType.Read, 'zone')
                ? [{ title: t('Zones'), href: `/companies/${companyId}/workspaces/zones` }]
                : []),
            ],
          },
        ]
      : []),
    ...(hasPermission(AccessType.Read, 'product')
      ? [
          {
            title: t('Devices'),
            icon: 'signal',
            href: `/companies/${companyId}/products`,
          },
        ]
      : []),
    ...(hasPermission(AccessType.Read, 'company')
      ? [
          {
            title: t('Company'),
            icon: 'building',
            href: `/companies/${companyId}/detail`,
          },
        ]
      : []),
    ...(hasPermission(AccessType.Read, 'user')
      ? [
          {
            title: t('Users'),
            href: `/companies/${companyId}/users`,
            icon: 'account',
          },
        ]
      : []),
    ...(isSysAdmin
      ? [
          {
            title: t('Care'),
            icon: 'eyeOpen',
            activeRoute: 'Care',
            active: pathname?.startsWith(`/companies/${companyId}/care`),
            items: [{ title: t('Live status'), href: `/companies/${companyId}/care/live` }],
          },
        ]
      : []),
    ...(hasPermission(AccessType.Read, 'settings')
      ? [
          {
            title: t('Settings'),
            icon: 'gear',
            active: false,
            key: 'settings',
            activeRoute: `/workspacetypes`,
            unfoldAtTop: true,
            style: styles.settings,
            textStyle: styles.settingsTitle,
            contentContainerStyle: styles.settingsRow,
            items: [
              {
                title: t('Workspace types'),
                href: { pathname: `/workspacetypes`, params: { companyId } },
              },
            ],
          },
        ]
      : []),
  ]

  return [...items, ...(otherItems || [])].map(menuDataForItem)
}
